import { useState, useEffect } from 'react';
import { ExternalLink, Loader, ChevronDown, AlertCircle, TrendingUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { createParlayLink, BOOKMAKERS } from '../../services/oddsApiClient';
import { parseBetWithAI, enhanceBetWithOdds } from '../../services/betParser';

const BookmakerLinks = ({ bet, allBets = [], isParlay = false, compact = false }) => {
  const [bookmakerLinks, setBookmakerLinks] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedBookmaker, setSelectedBookmaker] = useState('fanduel');
  const [bestOddsBookmaker, setBestOddsBookmaker] = useState(null);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);

  useEffect(() => {
    // console.log('BookmakerLinks - Bet data:', bet);

    // Check if we already have parsed data with bookmaker info
    if (bet?.parsedData?.bookmakers) {
      // console.log('BookmakerLinks - Using parsed bookmaker data:', bet.parsedData.bookmakers);
      setBookmakerLinks(bet.parsedData.bookmakers);

      // Find best odds bookmaker
      const bestBookmaker = findBestOdds(bet.parsedData.bookmakers);
      setBestOddsBookmaker(bestBookmaker);

      // Auto-select best odds bookmaker if available
      if (bestBookmaker) {
        setSelectedBookmaker(bestBookmaker);
      }

      setIsLoading(false);

      // Show success animation when odds are loaded
      if (Object.keys(bet.parsedData.bookmakers).length > 0) {
        setShowSuccessAnimation(true);
        setTimeout(() => setShowSuccessAnimation(false), 2000);
      }
    } else if (bet?.text && !bet?.isParsing) {
      // console.log('BookmakerLinks - No parsed data, fetching manually');
      // Fallback to fetching if no parsed data
      fetchBookmakerLinks();
    }
  }, [bet]);

  // Find bookmaker with best odds (highest positive or least negative)
  const findBestOdds = (bookmakers) => {
    if (!bookmakers || Object.keys(bookmakers).length === 0) return null;

    let bestBookmaker = null;
    let bestPrice = -Infinity;

    Object.entries(bookmakers).forEach(([key, data]) => {
      if (data?.price !== undefined && data.price > bestPrice) {
        bestPrice = data.price;
        bestBookmaker = key;
      }
    });

    return bestBookmaker;
  };

  const fetchBookmakerLinks = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // console.log('BookmakerLinks - Fetching manually for bet:', bet.text);

      // Use the enhanced betParser that handles player props
      const parsedData = await parseBetWithAI(bet.text);
      const enhancedData = await enhanceBetWithOdds(parsedData);

      // console.log('BookmakerLinks - Enhanced data:', enhancedData);

      if (enhancedData?.bookmakers) {
        setBookmakerLinks(enhancedData.bookmakers);

        // Find and set best odds
        const bestBookmaker = findBestOdds(enhancedData.bookmakers);
        setBestOddsBookmaker(bestBookmaker);
        if (bestBookmaker) {
          setSelectedBookmaker(bestBookmaker);
        }
      } else {
        setError('No odds found for this bet');
      }

    } catch (err) {
      console.error('Error fetching bookmaker links:', err);
      setError('Failed to fetch bookmaker links');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBookmakerClick = (bookmakerKey) => {
    const bookmaker = bookmakerLinks[bookmakerKey];

    // console.log('BookmakerLinks - handleBookmakerClick:', {
    //   bookmakerKey,
    //   bookmaker,
    //   hasDirectLink: !!bookmaker?.direct_link,
    //   hasLink: !!bookmaker?.link,
    //   allBookmakerLinks: bookmakerLinks,
    //   isPlayerProp: bet?.parsedData?.bet_type === 'player_prop'
    // });

    // Use direct link if available (primary method from betParser)
    if (bookmaker?.direct_link) {
      // console.log('BookmakerLinks - Opening direct_link:', bookmaker.direct_link);
      window.open(bookmaker.direct_link, '_blank', 'noopener,noreferrer');
      return;
    }

    // Fallback to link property
    if (bookmaker?.link) {
      // console.log('BookmakerLinks - Opening link:', bookmaker.link);
      window.open(bookmaker.link, '_blank', 'noopener,noreferrer');
      return;
    }

    // Parlay handling with proper hierarchy
    if (isParlay && allBets.length > 1) {
      // console.log('BookmakerLinks - Attempting parlay construction');
      const parlaySelections = allBets.map(bet => {
        const bookmakerData = bet.parsedData?.bookmakers?.[bookmakerKey];
        if (!bookmakerData) return null;

        // Build selection with proper hierarchy for parlay
        return {
          hierarchy: {
            marketId: bookmakerData.market_sid,
            selectionId: bookmakerData.selection_id,
            sids: {
              market: bookmakerData.market_sid,
              outcome: bookmakerData.selection_id
            }
          },
          market: { sid: bookmakerData.market_sid },
          outcome: {
            sid: bookmakerData.selection_id,
            link: bookmakerData.direct_link
          }
        };
      }).filter(Boolean);

      if (parlaySelections.length === allBets.length) {
        const parlayLink = createParlayLink(bookmakerKey, parlaySelections);
        if (parlayLink) {
          // console.log('BookmakerLinks - Opening parlay link:', parlayLink);
          window.open(parlayLink, '_blank', 'noopener,noreferrer');
          return;
        }
      }

      // console.log('BookmakerLinks - Falling back to homepage (parlay construction failed)');
      window.open(BOOKMAKERS[bookmakerKey]?.homepage || 'https://sportsbook.fanduel.com', '_blank', 'noopener,noreferrer');
    } else {
      // Final fallback to bookmaker homepage
      // console.log('BookmakerLinks - Falling back to homepage (no links available)');
      window.open(BOOKMAKERS[bookmakerKey]?.homepage || 'https://sportsbook.fanduel.com', '_blank', 'noopener,noreferrer');
    }
  };

  // Check if bet is still being parsed
  if (bet?.isParsing) {
    return (
      <div className={`flex items-center ${compact ? 'justify-start py-1' : 'justify-center py-2'}`}>
        <Loader className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} text-violet-400 animate-spin`} />
        <span className={`ml-2 ${compact ? 'text-[11px]' : 'text-xs'} text-white/60`}>
          {compact ? 'Analyzing...' : 'Analyzing bet...'}
        </span>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={`flex items-center ${compact ? 'justify-start py-1' : 'justify-center py-2'}`}>
        <Loader className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} text-emerald-400 animate-spin`} />
        <span className={`ml-2 ${compact ? 'text-[11px]' : 'text-xs'} text-white/60`}>
          {compact ? 'Finding odds...' : 'Finding best odds...'}
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center gap-2 ${compact ? 'text-[11px]' : 'text-xs'} text-yellow-400 py-1`}>
        <AlertCircle className={compact ? "w-3 h-3" : "w-3 h-3"} />
        <span>{error}</span>
      </div>
    );
  }

  const availableBookmakers = Object.keys(bookmakerLinks);
  const hasLinks = availableBookmakers.length > 0;
  const isBestOdds = selectedBookmaker === bestOddsBookmaker;
  const isPlayerProp = bet?.parsedData?.bet_type === 'player_prop';

  return (
    <div className={compact ? "space-y-1.5" : "mt-2 space-y-2"}>
      {hasLinks ? (
        <>
          {/* Primary bookmaker button */}
          <div className="flex items-center gap-2">
            <motion.button
              onClick={() => handleBookmakerClick(selectedBookmaker)}
              className={`flex-1 flex items-center justify-center gap-1.5 ${
                compact ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-1.5 text-sm'
              } rounded-lg bg-gradient-to-r ${
                isPlayerProp
                  ? 'from-purple-600 to-purple-500'
                  : BOOKMAKERS[selectedBookmaker]?.color || 'from-emerald-600 to-emerald-500'
              } ${BOOKMAKERS[selectedBookmaker]?.textColor || 'text-white'} hover:opacity-90 transition-all shadow-sm font-medium relative overflow-hidden group`}
              disabled={!bookmakerLinks[selectedBookmaker]}
              animate={showSuccessAnimation ? { scale: [1, 1.05, 1] } : {}}
              transition={{ duration: 0.3 }}
            >
              {/* Shimmer effect on hover */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity -translate-x-full group-hover:translate-x-full duration-700" />

              {/* Success pulse ring */}
              {showSuccessAnimation && (
                <motion.div
                  className="absolute inset-0 rounded-lg border-2 border-emerald-400"
                  initial={{ opacity: 1, scale: 1 }}
                  animate={{ opacity: 0, scale: 1.2 }}
                  transition={{ duration: 0.6 }}
                />
              )}

              <span className="relative z-10">
                {BOOKMAKERS[selectedBookmaker]?.name || 'Bet Now'}
              </span>
              <ExternalLink className={compact ? "w-3 h-3" : "w-3.5 h-3.5"} />
              {bookmakerLinks[selectedBookmaker]?.price && (
                <span className="relative z-10 font-bold">
                  {bookmakerLinks[selectedBookmaker].price > 0 ? '+' : ''}
                  {bookmakerLinks[selectedBookmaker].price}
                </span>
              )}

              {/* Best odds badge */}
              {isBestOdds && availableBookmakers.length > 1 && (
                <span className="relative z-10 ml-1 flex items-center gap-0.5 bg-yellow-500/20 text-yellow-300 px-1.5 py-0.5 rounded text-[10px] font-semibold">
                  <TrendingUp className="w-2.5 h-2.5" />
                  Best
                </span>
              )}
            </motion.button>

            {/* Dropdown for other bookmakers */}
            {availableBookmakers.length > 1 && (
              <div className="relative">
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className={`${
                    compact ? 'p-1 text-xs' : 'p-1.5'
                  } bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors`}
                  title="More sportsbooks"
                >
                  <ChevronDown className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-white/80 transition-transform ${
                    showDropdown ? 'rotate-180' : ''
                  }`} />
                </button>

                <AnimatePresence>
                  {showDropdown && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      className="absolute right-0 top-full mt-1 w-52 bg-slate-800 border border-slate-700 rounded-lg shadow-2xl z-[100] overflow-hidden"
                      style={{ maxHeight: '240px' }}
                    >
                      <div className="max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-900">
                        {availableBookmakers.map(bookmakerKey => {
                          const bookmaker = bookmakerLinks[bookmakerKey];
                          const isSelected = bookmakerKey === selectedBookmaker;
                          const isBest = bookmakerKey === bestOddsBookmaker;

                          return (
                            <button
                              key={bookmakerKey}
                              onClick={() => {
                                setSelectedBookmaker(bookmakerKey);
                                setShowDropdown(false);
                                handleBookmakerClick(bookmakerKey);
                              }}
                              className={`w-full px-3 py-2 text-left transition-colors flex items-center justify-between group ${
                                isSelected
                                  ? 'bg-violet-600/20 hover:bg-violet-600/30'
                                  : 'hover:bg-slate-700'
                              }`}
                            >
                              <span className={`text-sm flex items-center gap-1.5 ${isSelected ? 'text-violet-300' : 'text-white/90'}`}>
                                {BOOKMAKERS[bookmakerKey].name}
                                {isSelected && ' ✓'}
                                {isBest && (
                                  <span className="text-[10px] bg-yellow-500/20 text-yellow-300 px-1 py-0.5 rounded">
                                    Best
                                  </span>
                                )}
                              </span>
                              {bookmaker.price && (
                                <span className={`text-xs font-medium ${
                                  isSelected ? 'text-violet-300' : isBest ? 'text-yellow-300' : 'text-emerald-400'
                                }`}>
                                  {bookmaker.price > 0 ? '+' : ''}{bookmaker.price}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>

          {/* Quick odds comparison - only show in non-compact mode */}
          {!compact && availableBookmakers.length > 2 && (
            <div className="flex items-center gap-2 text-xs text-white/60">
              <span>Compare:</span>
              <div className="flex gap-2 flex-wrap">
                {availableBookmakers.slice(0, 3).map(key => (
                  <span key={key} className={key === bestOddsBookmaker ? "text-yellow-300 font-medium" : "text-emerald-400"}>
                    {BOOKMAKERS[key].name}: {bookmakerLinks[key].price > 0 ? '+' : ''}{bookmakerLinks[key].price}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-xs text-white/50 py-1">
          No live odds available for this selection
        </div>
      )}

      {/* Parlay mode indicator */}
      {isParlay && allBets.length > 1 && (
        <div className="text-xs text-violet-400 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          <span>Parlay mode: {allBets.length} selections</span>
        </div>
      )}
    </div>
  );
};

export default BookmakerLinks;